import { Check, EyeOff, InfoCircle } from "@untitledui/icons";
import { type MouseEvent, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { RequestCustomRiskEditModal } from "@/pages/safety-beta/configure/components/RequestCustomRiskEditModal/RequestCustomRiskEditModal";
import { cx } from "@/utils/cx";
import type { KeywordGroup } from "./keyword-config";
import type { RiskItem, Severity } from "./risk-groups";
import {
  CREATOR_RISK_EXPLANATION_TOOLTIP,
  canEditCreatorRiskExplanation,
  RISK_EXPLANATION_DEFAULT_PLACEHOLDERS,
  SEVERITY_LABEL,
} from "./risk-groups";

// Read-only badge, not a severity picker — color stays local on purpose.
const SEVERITY_TEXT: Record<Severity, string> = {
  critical: "text-error-700",
  high: "text-error-700",
  medium: "text-warning-700",
  low: "text-success-700",
};

const SEVERITY_DOT: Record<Severity, string> = {
  critical: "bg-error-500",
  high: "bg-error-500",
  medium: "bg-warning-500",
  low: "bg-success-500",
};

export type RiskState = {
  enabled: boolean;
  autoConfirm: boolean;
  // Human-readable name of the safety policy this risk is mapped to, or null
  // when no policy has been assigned yet.
  policy: string | null;
  severity: Severity;
  // Configured keyword lists (keyword fields + placeholder domain knowledge),
  // shown read-only under the risk.
  keywords: KeywordGroup[];
  // Merchant-authored override for the {creator_risk_explanation} message
  // variable, or null when not set.
  customCreatorRiskExplanation: string | null;
};

type RiskRowProps = {
  item: RiskItem;
  state: RiskState;
  onClick: () => void;
};

// Falls back to the known pipeline default (currently just Unlicensed Music) so the
// read-only view matches what's actually sent even before a merchant overrides it —
// gated on `enabled` since a disabled risk isn't sending anything.
const getCreatorRiskExplanation = (item: RiskItem, state: RiskState): string | null => {
  if (!state.enabled || !canEditCreatorRiskExplanation(item)) return null;
  return state.customCreatorRiskExplanation ?? RISK_EXPLANATION_DEFAULT_PLACEHOLDERS[item.key] ?? null;
};

export const RiskRow = ({ item, state, onClick }: RiskRowProps) => {
  const creatorRiskExplanation = getCreatorRiskExplanation(item, state);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const isCustom = item.category === "custom";

  return (
    <div className="border-b border-border-secondary last:border-b-0">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: contains its own focusable tooltip triggers, so this can't be a <button> */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: see above */}
      <div
        onClick={onClick}
        className="group relative flex items-start gap-3 p-6 cursor-pointer hover:bg-unbox-cream-50 transition-colors"
      >
        {/* Status icon */}
        <span aria-hidden="true" className="shrink-0">
          {state.enabled ? (
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-success-50">
              <Check className="w-4 h-4 text-success-600" />
            </span>
          ) : (
            <span className="flex items-center justify-center w-6 h-6">
              <EyeOff className="w-4 h-4 text-quaternary" />
            </span>
          )}
        </span>

        {/* Name + severity + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className={cx("text-base font-medium leading-snug", state.enabled ? "text-primary" : "text-tertiary")}>
              {item.name}
            </p>
            {state.enabled && (
              <span className="inline-flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-full">
                <span aria-hidden="true" className={cx("w-1.5 h-1.5 rounded-full", SEVERITY_DOT[state.severity])} />
                <span className={cx("text-xs font-medium whitespace-nowrap", SEVERITY_TEXT[state.severity])}>
                  {SEVERITY_LABEL[state.severity]} Severity
                </span>
              </span>
            )}
          </div>
          <p className="text-sm text-quaternary mt-1 leading-relaxed">{item.desc}</p>

          {creatorRiskExplanation && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1 text-xs font-medium text-tertiary">
                Risk explanation for creators
                <Tooltip title={CREATOR_RISK_EXPLANATION_TOOLTIP}>
                  <TooltipTrigger className="cursor-pointer text-fg-disabled hover:text-fg-quaternary_hover">
                    <InfoCircle className="w-3.5 h-3.5" />
                  </TooltipTrigger>
                </Tooltip>
              </span>
              <p className="mt-1 px-2.5 py-1.5 rounded-md border border-border-secondary bg-secondary text-xs text-secondary">
                {creatorRiskExplanation}
              </p>
            </div>
          )}

          {/* Configured keyword lists (read-only) */}
          {state.enabled && state.keywords.some((g) => g.values.length > 0) && (
            <div className="mt-3 flex flex-col gap-2">
              {state.keywords
                .filter((g) => g.values.length > 0)
                .map((g) => (
                  <div key={g.key} className="flex flex-wrap items-center gap-2">
                    {g.values.map((v) => (
                      <span
                        key={v}
                        className="px-2.5 py-0.5 rounded-sm border border-border-primary bg-white text-sm font-medium text-secondary"
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Read-only config summary */}
        {state.enabled ? (
          <div className="flex items-start gap-6 shrink-0">
            <div className="flex flex-col gap-1.5 w-[180px]">
              <span className="text-xs text-primary">When risk is detected</span>
              <Tooltip title={state.autoConfirm ? "Auto-approve" : "Require review"}>
                <TooltipTrigger className="inline-flex self-start max-w-full min-w-0">
                  <span className="truncate h-6 flex items-center px-3 rounded-full border border-border-secondary bg-secondary text-sm font-medium text-secondary">
                    {state.autoConfirm ? "Auto-approve" : "Require review"}
                  </span>
                </TooltipTrigger>
              </Tooltip>
            </div>
            <div className="flex flex-col gap-1.5 w-[180px]">
              <span className="text-xs text-primary">If risk is confirmed</span>
              <Tooltip title={state.policy ?? "No policy"}>
                <TooltipTrigger className="inline-flex self-start max-w-full min-w-0">
                  <span
                    className={cx(
                      "truncate h-6 flex items-center px-3 rounded-full border border-border-secondary bg-secondary text-sm font-medium",
                      state.policy ? "text-secondary" : "text-quaternary",
                    )}
                  >
                    {state.policy ?? "No policy"}
                  </span>
                </TooltipTrigger>
              </Tooltip>
            </div>
          </div>
        ) : (
          <span className="shrink-0 h-6 flex items-center px-3 rounded-full border border-border-secondary bg-secondary text-sm font-medium text-secondary">
            Disabled
          </span>
        )}

        {isCustom && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end rounded-r-lg bg-gradient-to-l from-unbox-cream-50 from-50% to-transparent pl-24 pr-6 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <Button
              color="primary"
              size="md"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setEditModalOpen(true);
              }}
              className="pointer-events-auto"
            >
              Request edit to risk
            </Button>
          </div>
        )}
      </div>

      {isCustom && (
        <RequestCustomRiskEditModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          risk={{ id: item.key, name: item.name }}
        />
      )}
    </div>
  );
};
